import type { ReactElement } from 'react';
import type { HeroPlugin, EquippedLook } from './contract';
import { lookItem } from './contract';
import { heroSkinAssetsFor } from '../content/heroes/assets';

/**
 * THE SHARED SKIN RENDERER (ADR-0116) — the ONE render plugin every "skin" hero uses. A skin hero's figure is
 * a painted IMAGE (not hand-drawn SVG) and is FULL-LOOK-SWAP: equipping the hero's `<archetype>_look` place
 * replaces the whole figure with that look's complete image (one at a time). Every skin hero rendered
 * identically — only its art (`art/base.webp` + `art/looks/*.webp`) and its theme differ — so the render code
 * is HERO-AGNOSTIC GLUE here, not copy-pasted per hero. `art/plugins.ts` builds one of these for every hero
 * that ships raster art but no bespoke `skin.tsx`, so a plain skin hero is a pure DIRECTORY DROP (no code).
 *
 * A genuinely bespoke hero (a vector body, layered cosmetics, a hand-drawn scene) still authors its own
 * `content/heroes/<hero>/skin.tsx` exporting a {@link HeroPlugin}; the registry prefers it over this default.
 * So per-hero render code is the EXCEPTION (bespoke heroes), not the rule (skin heroes).
 */

/** The full square art canvas (the webp is 1:1) — the catalog tiles show the whole figure. */
const SQUARE_VIEWBOX = '0 0 100 100';
/**
 * The FIGURE viewBox — a PORTRAIT crop of the square art (the stage slots are tall + bottom-anchored, so a
 * square figure reads small + floats high). This windows out the dead side margins so the hero fills the stage
 * top-to-bottom, grounded on the scene. Feet sit near y96, so the window ends just below them; it keeps almost
 * the full width (only the very tips of the widest winged looks fall outside).
 */
const FIGURE_VIEWBOX = '7 0 86 98';
/** The avatar crop — the chibi head + shoulders fill the top ~45% of the square (the circular bust). */
const AVATAR_VIEWBOX = '26 4 48 48';

/** The hero's look-place id — equipping it swaps the whole figure (full-look-swap). */
const lookPlaceFor = (archetype: string): string => `${archetype}_look`;

/** A painted image filling the unit box (meet → never cropped; the art's own transparent margin frames it). */
function paintedImage(href: string): ReactElement {
  return (
    <image href={href} x="0" y="0" width="100" height="100" preserveAspectRatio="xMidYMid meet" />
  );
}

/**
 * The CHEER burst (ADR-0102) — soft sparkles around the hero when a fresh effort lands (`mood === 'cheer'`).
 * The fill follows the hero's own accent (the applied theme's `--color-accent`), so each hero cheers in its
 * own colour with no per-hero code; the literal is a safe fallback for any unthemed render context.
 */
const CHEER = (
  <g fill="var(--color-accent, #ffd45e)" aria-hidden="true">
    {[
      { x: 20, y: 18, r: 2.4, d: '0s' },
      { x: 80, y: 14, r: 2.8, d: '.25s' },
      { x: 86, y: 46, r: 2, d: '.45s' },
      { x: 14, y: 44, r: 2.2, d: '.15s' },
    ].map((p, i) => (
      <circle
        key={i}
        cx={p.x}
        cy={p.y}
        r={p.r}
        style={{
          transformOrigin: `${p.x}px ${p.y}px`,
          animation: `jewel 1.4s ease-in-out ${p.d} infinite`,
        }}
      />
    ))}
  </g>
);

/**
 * Build the shared skin plugin for one archetype. Resolves the hero's raster art once, then draws the figure /
 * avatar / item tiles from it. `renderScene` is omitted — backgrounds are a cross-hero type drawn by the stage
 * (ADR-0110 §7), so the resolver fills it with a no-op.
 */
export function createSkinPlugin(archetype: string): HeroPlugin {
  const { base, looks } = heroSkinAssetsFor(archetype);
  const place = lookPlaceFor(archetype);

  /** The figure image for the equipped look — the chosen full-look swap, else the bare base figure. */
  const figureUrl = (look: EquippedLook): string => {
    const worn = lookItem(look, place);
    return (worn && looks[worn]) || base;
  };

  return {
    archetype,
    renderFigure({ look = {}, className, mood }) {
      return (
        <svg viewBox={FIGURE_VIEWBOX} className={className} aria-hidden="true">
          {paintedImage(figureUrl(look))}
          {mood === 'cheer' && CHEER}
        </svg>
      );
    },
    renderAvatar({ look = {} }) {
      return (
        <svg viewBox={AVATAR_VIEWBOX} preserveAspectRatio="xMidYMid slice">
          {paintedImage(figureUrl(look))}
        </svg>
      );
    },
    renderItemIcon({ slug, className = 'size-14' }) {
      // The wardrobe TILE: a look shows the whole mini-figure wearing it (the most honest preview of a
      // full-look swap). Falls back to an empty box if the slug has no asset (e.g. before art is dropped in).
      const url = looks[slug];
      return (
        <svg viewBox={SQUARE_VIEWBOX} className={className} aria-hidden="true">
          {url && paintedImage(url)}
        </svg>
      );
    },
  };
}
